import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import PublicNav from 'layouts/PublicNav';
import PublicFooter from 'layouts/PublicFooter';
import useScrollReveal from 'hooks/useScrollReveal';
import './SolutionPageTemplate.css';

// Detect language from the first non-empty line so callers don't have to pass it.
function detectLang(code) {
  if (!code) return 'text';
  const first = code.trim().split('\n')[0] || '';
  if (first.startsWith('#') || first.startsWith('$') || first.startsWith('zibby ') || first.startsWith('npm ') || first.startsWith('curl ')) return 'bash';
  if (first.includes('//') || first.includes('import ') || first.includes('const ') || first.includes('export ')) return 'javascript';
  return 'javascript';
}

/**
 * Shared template for /solutions/* pages so they all feel like one product.
 *
 * Props:
 *   eyebrow   — small uppercase tag above the headline (e.g. "PLATFORM")
 *   title     — main headline (serif)
 *   lede      — paragraph under headline
 *   ctaPrimary, ctaSecondary — { label, href }
 *   sections  — array of { title, body, code? } rendered as alternating blocks
 *   cta       — final CTA block { title, body, primary, secondary }
 *   meta      — { title, description } for <Helmet>
 */
const SolutionPageTemplate = ({
  eyebrow,
  title,
  lede,
  ctaPrimary,
  ctaSecondary,
  sections = [],
  cta,
  meta,
}) => {
  const pageRef = useScrollReveal();
  return (
    <>
      <Helmet>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
      </Helmet>
      <div className="public-page sp-page fade-in" ref={pageRef}>
        <PublicNav />

        {/* Hero */}
        <section className="sp-hero">
          <div className="sp-hero-inner">
            {eyebrow && <div className="sp-eyebrow reveal">{eyebrow}</div>}
            <h1 className="sp-title reveal reveal-delay-1">{title}</h1>
            <p className="sp-lede reveal reveal-delay-2">{lede}</p>
            {(ctaPrimary || ctaSecondary) && (
              <div className="sp-hero-actions reveal reveal-delay-3">
                {ctaPrimary && (
                  <a href={ctaPrimary.href} className="sp-btn sp-btn-primary"
                     {...(ctaPrimary.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
                    {ctaPrimary.label}
                  </a>
                )}
                {ctaSecondary && (
                  <a href={ctaSecondary.href} className="sp-btn sp-btn-secondary"
                     {...(ctaSecondary.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
                    {ctaSecondary.label}
                  </a>
                )}
              </div>
            )}
          </div>
        </section>

        {/* Body sections */}
        {sections.map((s, i) => (
          <section key={i} className={`sp-section ${i % 2 === 1 ? 'sp-section-alt' : ''}`}>
            <div className="sp-section-inner">
              <div className="sp-section-text">
                <h2 className="sp-section-title reveal">{s.title}</h2>
                <div className="sp-section-body reveal reveal-delay-1">{s.body}</div>
              </div>
              {s.code && (
                <div className="sp-section-code reveal reveal-delay-2">
                  <SyntaxHighlighter
                    language={s.lang || detectLang(s.code)}
                    style={vscDarkPlus}
                    wrapLongLines
                    customStyle={{
                      background: 'transparent',
                      margin: 0,
                      padding: 0,
                      fontSize: '13px',
                      lineHeight: '1.6',
                    }}
                    codeTagProps={{ style: { fontFamily: '"SF Mono", Menlo, Monaco, Consolas, monospace' } }}
                  >
                    {s.code}
                  </SyntaxHighlighter>
                </div>
              )}
              {s.visual && (
                <div className="sp-section-visual reveal reveal-delay-2">
                  {s.visual}
                </div>
              )}
            </div>
          </section>
        ))}

        {/* Final CTA */}
        {cta && (
          <section className="sp-cta">
            <div className="sp-cta-inner">
              <h2 className="sp-cta-title reveal">{cta.title}</h2>
              {cta.body && <p className="sp-cta-body reveal reveal-delay-1">{cta.body}</p>}
              <div className="sp-cta-actions reveal reveal-delay-2">
                {cta.primary && (
                  <a href={cta.primary.href} className="sp-btn sp-btn-primary"
                     {...(cta.primary.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
                    {cta.primary.label}
                  </a>
                )}
                {cta.secondary && (
                  <a href={cta.secondary.href} className="sp-btn sp-btn-secondary"
                     {...(cta.secondary.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
                    {cta.secondary.label}
                  </a>
                )}
              </div>
            </div>
          </section>
        )}

        <PublicFooter />
      </div>
    </>
  );
};

export default SolutionPageTemplate;
